import classNames from "classnames";
import { Suspense, useRef } from "react";
import { Close } from "@mui/icons-material";
import { Box, DialogContent, DialogTitle, IconButton, Stack, Typography } from "@mui/material";
import { AvatarWithSideContent } from "components/layout/avatar/AvatarWithSideContent";
import { makeStyles } from "components/providers/makeStyles";
import { T } from "components/util/t";
import { RankingsBreakdown } from "./RankingsBreakdown";
import { PoweredBy } from "components/layout/PoweredBy";
import { RankingsSummaryCompact } from "./RankingsSummaryCompact";
import { DesktopOrMobileModal } from "components/layout/modal/DesktopOrMobileModal";
import { useSuspenseQuery } from "@apollo/client";
import { GET_SERIES_ATHLETE_RANKING_RESULTS } from "graphql/series";
import { RankingsDialogSkeleton } from "./RankingsDialogSkeleton";
import { useHasMoreToScroll } from "hooks/useHasMoreToScroll";
import { useIsMobile } from "hooks/useIsMobile";
import { RankingsSummary } from "./RankingsSummary";
import { useMergeRefs } from "hooks/useMergeRefs";
import { useRenderCompactSummary } from "./useRenderCompactSummary";

export const RankingsDialog = ({ dialogOpen, closeDialog, ranking, selectedSeries, selectedDivision, filter }) => (
    <DesktopOrMobileModal
        open={dialogOpen}
        closeModal={closeDialog}
        aria-labelledby="athlete-rankings-dialog-title"
        maxWidth="md"
    >
        <Suspense fallback={<RankingsDialogSkeleton/>}>
            <Content
                ranking={ranking}
                closeDialog={closeDialog}
                selectedSeries={selectedSeries}
                selectedDivision={selectedDivision}
                filter={filter}
            />
        </Suspense>
    </DesktopOrMobileModal>
);

const useStyles = makeStyles(theme => ({
    title: {
        boxShadow: theme.shadows.custom[1]
    },
    footer: {
        boxShadow: theme.shadows.custom[2]
    }
}));

const Content = ({ ranking, closeDialog, selectedSeries, selectedDivision, filter }) => {
    const { data: { series: { athleteRankingResults } } } = useSuspenseQuery(GET_SERIES_ATHLETE_RANKING_RESULTS, { variables: { id: selectedSeries.id, divisionId: selectedDivision.id, athleteId: ranking.athlete.id, filter }, fetchPolicy: "cache-and-network", nextFetchPolicy: "cache-first" });
    const classes = useStyles();
    const isMobile = useIsMobile();
    const rankingsSummaryRef = useRef();

    const { scrollerRef: scrollerRef1, renderCompactSummary } = useRenderCompactSummary(rankingsSummaryRef, isMobile);
    const { scrollerRef: scrollerRef2, hasTopScroll, hasBottomScroll } = useHasMoreToScroll("vertical");

    const { resultsToCount, eligibleResults, eventsToCount: eventsToCountIds = [], results } = athleteRankingResults;
    const eventsToCount = eventsToCountIds.map(eventId => results.find(result => result.eventDivision.event.id === eventId)?.eventDivision.event.name).filter(v => v);

    const scrollerRef = useMergeRefs(scrollerRef1, scrollerRef2);

    return (
        <>
            <DialogTitle id="athlete-rankings-dialog-title" className={classNames("sticky-dialog-title", { [classes.title]: hasTopScroll })}>
                <Stack direction="row" alignItems="center" spacing={2}>
                    <Box minWidth="55%" flex={1}>
                        <AvatarWithSideContent
                            athlete={ranking.athlete}
                            title={<Typography variant={renderCompactSummary ? "h7" : "h4"}>{ranking.place === 1 ? `${ranking.athlete.name} 🏆` : ranking.athlete.name}</Typography>}
                            subtitle={ranking.displayProperty && <Typography color="textSecondary">{ranking.displayProperty}</Typography>}
                            avatarProps={{ size: renderCompactSummary ? undefined : "lg" }}
                        />
                    </Box>
                    {renderCompactSummary && !isMobile && <RankingsSummaryCompact selectedSeries={selectedSeries} selectedDivision={selectedDivision} ranking={ranking}/>}
                    <Box component={IconButton} color="inherit" size="small" onClick={closeDialog} aria-label="close"><Close/></Box>
                </Stack>
                {renderCompactSummary && isMobile && <RankingsSummaryCompact selectedSeries={selectedSeries} selectedDivision={selectedDivision} ranking={ranking} className={classes.title} isMobile/>}
            </DialogTitle>

            <DialogContent ref={scrollerRef}>
                <RankingsSummary selectedSeries={selectedSeries} selectedDivision={selectedDivision} ranking={ranking} ref={rankingsSummaryRef} />
                <RankingsBreakdown results={results} />
            </DialogContent>

            <Stack spacing={3} paddingX={3} paddingTop={2} paddingBottom={3} className={classNames({ [classes.footer]: hasBottomScroll })}>
                {resultsToCount && !eligibleResults && (
                    <Typography variant="caption" color="text.secondary" marginRight="auto">
                        <T count={resultsToCount} eventsToCount={eventsToCount}>counted_results_note</T>
                    </Typography>
                )}
                {!resultsToCount && eligibleResults && (
                    <Typography variant="caption" color="text.secondary" marginRight="auto">
                        <T eligible={eligibleResults} eventsToCount={eventsToCount}>eligible_results_note</T>
                    </Typography>
                )}
                {resultsToCount && eligibleResults && (
                    <Typography variant="caption" color="text.secondary" marginRight="auto">
                        <T count={resultsToCount} eligible={eligibleResults} eventsToCount={eventsToCount}>eligible_and_counted_results_note</T>
                    </Typography>
                )}
                <PoweredBy sticky={false} variant="secondary" />
            </Stack>
        </>
    );
};
